import { useState, useEffect } from 'react';
import { formatTimestamp } from '../../utils/date';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Switch,
  Button,
  Space,
  Alert,
  Divider,
  Select,
  App,
  Modal,
  Spin,
  Tag,
  Tabs,
  Row,
  Col,
  Typography,
  Tooltip,
  Popconfirm,
  Statistic,
  Table,
  Badge,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudOutlined,
  DatabaseOutlined,
  ApiOutlined,
  SaveOutlined,
  ReloadOutlined,
  SyncOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  SearchOutlined,
  DownloadOutlined,
  KeyOutlined,
  CopyOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  getProviderStatus,
  getIcecatSettings,
  updateIcecatSettings,
  testIcecatConnection,
  getIcecatCacheStats,
  clearIcecatCache,
  searchIcecatDirect,
  startIcecatPreseed,
  getIcecatPreseedJobs,
  startIcecatBackfill,
  getIcecatBackfillJobs,
  getIcecatQueueStats,
  getEtlSettings,
  updateEtlSettings,
  setProviderEnabled,
  getHpSettings,
  updateHpSettings,
  testHpConnection,
  startHpSync,
  cancelHpSync,
  getHpSyncJobs,
  generateHpSshKey,
  getHpProductStats,
  type ProviderStatus,
  type IcecatSettings,
  type IcecatCacheStats,
  type IcecatPreseedJob,
  type IcecatBackfillJob,
  type IcecatQueueStats,
  type EtlSettings,
  type HpSettings,
  type HpSyncJob,
  type HpProductStats,
} from '../../api/providers';

const { Text, Paragraph } = Typography;

export function ProvidersTab() {
  const { message } = App.useApp();
  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem('pim_providers_tab') || 'icecat'
  );
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    localStorage.setItem('pim_providers_tab', key);
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingIcecat, setTestingIcecat] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [searching, setSearching] = useState(false);
  const [preseeding, setPreseeding] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [providerStatus, setProviderStatus] = useState<Record<string, ProviderStatus>>({});
  const [icecatSettings, setIcecatSettings] = useState<IcecatSettings | null>(null);
  const [icecatCacheStats, setIcecatCacheStats] = useState<IcecatCacheStats | null>(null);
  const [preseedJobs, setPreseedJobs] = useState<IcecatPreseedJob[]>([]);
  const [backfillJobs, setBackfillJobs] = useState<IcecatBackfillJob[]>([]);
  const [queueStats, setQueueStats] = useState<IcecatQueueStats | null>(null);
  const [searchResult, setSearchResult] = useState<{ found: boolean; product?: unknown } | null>(null);
  const [etlSettings, setEtlSettings] = useState<EtlSettings | null>(null);
  const [hpSettings, setHpSettings] = useState<HpSettings | null>(null);
  const [hpSyncJobs, setHpSyncJobs] = useState<HpSyncJob[]>([]);
  const [testingHp, setTestingHp] = useState(false);
  const [syncingHp, setSyncingHp] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [hpPublicKey, setHpPublicKey] = useState<string>('');
  const [hpStats, setHpStats] = useState<HpProductStats | null>(null);

  const [icecatForm] = Form.useForm<IcecatSettings>();
  const [etlForm] = Form.useForm<EtlSettings>();
  const [hpForm] = Form.useForm<HpSettings>();
  const [searchForm] = Form.useForm();
  const [preseedForm] = Form.useForm();
  const [backfillForm] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [status, icecat, cacheStats, preseedJobsResult, backfillJobsResult, queueStatsResult, etl, hp, hpJobs, hpStatsResult] = await Promise.all([
        getProviderStatus(),
        getIcecatSettings(),
        getIcecatCacheStats(),
        getIcecatPreseedJobs(),
        getIcecatBackfillJobs(),
        getIcecatQueueStats(),
        getEtlSettings(),
        getHpSettings().catch(() => null),
        getHpSyncJobs().catch(() => ({ jobs: [] })),
        getHpProductStats().catch(() => null),
      ]);
      setProviderStatus(status);
      setIcecatSettings(icecat);
      setIcecatCacheStats(cacheStats);
      setPreseedJobs(preseedJobsResult.jobs || []);
      setBackfillJobs(backfillJobsResult.jobs || []);
      setQueueStats(queueStatsResult);
      setEtlSettings(etl);
      setHpSettings(hp);
      setHpSyncJobs(hpJobs.jobs || []);
      if (hpStatsResult) setHpStats(hpStatsResult);
      if (hp?.public_key) setHpPublicKey(hp.public_key);
      icecatForm.setFieldsValue(icecat);
      etlForm.setFieldsValue(etl);
      if (hp) hpForm.setFieldsValue(hp);
    } catch (error) {
      console.error('Failed to fetch provider settings:', error);
      message.error('Failed to load provider settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleIcecatSave = async (values: IcecatSettings) => {
    setSaving(true);
    try {
      const response = await updateIcecatSettings(values);
      if (response.status === 'success') {
        message.success('IceCat settings saved');
        fetchData();
      } else {
        message.error(response.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save IceCat settings:', error);
      message.error('Failed to save IceCat settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestIcecat = async () => {
    setTestingIcecat(true);
    try {
      const values = icecatForm.getFieldsValue();
      const response = await testIcecatConnection(values);
      if (response.status === 'success') {
        Modal.confirm({
          title: 'Connection Successful',
          content: response.message || 'IceCat API connection verified.',
          okText: 'Save Settings',
          cancelText: 'Don\'t Save',
          onOk: async () => {
            await handleIcecatSave(values);
          },
        });
      } else {
        message.error(response.message || response.error || 'IceCat connection failed');
      }
    } catch (error) {
      console.error('Failed to test IceCat connection:', error);
      message.error('Failed to test IceCat connection');
    } finally {
      setTestingIcecat(false);
    }
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    try {
      const response = await clearIcecatCache();
      if (response.status === 'success') {
        message.success(response.message || 'IceCat cache cleared');
        // Refresh cache stats
        const stats = await getIcecatCacheStats();
        setIcecatCacheStats(stats);
      } else {
        message.error(response.error || 'Failed to clear cache');
      }
    } catch (error) {
      console.error('Failed to clear IceCat cache:', error);
      message.error('Failed to clear IceCat cache');
    } finally {
      setClearingCache(false);
    }
  };

  const handleSearch = async (values: { mpn?: string; brand?: string; gtin?: string }) => {
    setSearching(true);
    setSearchResult(null);
    try {
      const response = await searchIcecatDirect(values);
      if (response.status === 'success') {
        setSearchResult({ found: response.found || false, product: response.product });
        if (response.found) {
          message.success('Product found in IceCat!');
          // Refresh cache stats since we may have cached a new product
          const stats = await getIcecatCacheStats();
          setIcecatCacheStats(stats);
        } else {
          message.info('Product not found in IceCat');
        }
      } else {
        message.error(response.error || 'Search failed');
      }
    } catch (error) {
      console.error('IceCat search failed:', error);
      message.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handlePreseed = async (values: { brand: string }) => {
    setPreseeding(true);
    try {
      const response = await startIcecatPreseed(values.brand);
      if (response.status === 'success') {
        message.success(`Pre-seed job started for "${values.brand}"`);
        preseedForm.resetFields();
        // Refresh jobs list
        const jobs = await getIcecatPreseedJobs();
        setPreseedJobs(jobs.jobs || []);
      } else {
        message.error(response.error || 'Failed to start pre-seed job');
      }
    } catch (error) {
      console.error('Failed to start pre-seed:', error);
      message.error('Failed to start pre-seed job');
    } finally {
      setPreseeding(false);
    }
  };

  const refreshPreseedJobs = async () => {
    try {
      const jobs = await getIcecatPreseedJobs();
      setPreseedJobs(jobs.jobs || []);
    } catch (error) {
      console.error('Failed to refresh pre-seed jobs:', error);
    }
  };

  const handleBackfill = async (values: { source_provider: string }) => {
    setBackfilling(true);
    try {
      const response = await startIcecatBackfill(values.source_provider);
      if (response.status === 'success') {
        message.success(`Backfill job started for ${values.source_provider} products`);
        backfillForm.resetFields();
        // Refresh jobs list
        const jobs = await getIcecatBackfillJobs();
        setBackfillJobs(jobs.jobs || []);
      } else {
        message.error(response.error || 'Failed to start backfill job');
      }
    } catch (error) {
      console.error('Failed to start backfill:', error);
      message.error('Failed to start backfill job');
    } finally {
      setBackfilling(false);
    }
  };

  const refreshBackfillJobs = async () => {
    try {
      const [jobs, stats] = await Promise.all([
        getIcecatBackfillJobs(),
        getIcecatQueueStats(),
      ]);
      setBackfillJobs(jobs.jobs || []);
      setQueueStats(stats);
    } catch (error) {
      console.error('Failed to refresh backfill jobs:', error);
    }
  };

  const handleEtlSave = async (values: EtlSettings) => {
    setSaving(true);
    try {
      const response = await updateEtlSettings(values);
      if (response.status === 'success') {
        message.success('NIQ Brandbank settings saved');
        fetchData();
      } else {
        message.error(response.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save ETL settings:', error);
      message.error('Failed to save NIQ Brandbank settings');
    } finally {
      setSaving(false);
    }
  };

  // HP handlers
  const handleHpSave = async (values: HpSettings) => {
    setSaving(true);
    try {
      const response = await updateHpSettings(values);
      if (response.status === 'success') {
        message.success('HP settings saved');
        fetchData();
      } else {
        message.error(response.error || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save HP settings:', error);
      message.error('Failed to save HP settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestHp = async () => {
    setTestingHp(true);
    try {
      const values = hpForm.getFieldsValue();
      const response = await testHpConnection(values);
      if (response.status === 'success') {
        Modal.confirm({
          title: 'Connection Successful',
          content: response.message || 'SFTP connection verified.',
          okText: 'Save Settings',
          cancelText: "Don't Save",
          onOk: async () => {
            await handleHpSave(values);
          },
        });
      } else {
        message.error(response.message || response.error || 'SFTP connection failed');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'SFTP connection failed');
    } finally {
      setTestingHp(false);
    }
  };

  const handleHpSync = async () => {
    setSyncingHp(true);
    try {
      const response = await startHpSync();
      if (response.status === 'success') {
        message.success(response.message || 'HP sync started');
        // Refresh job list after a short delay
        setTimeout(() => fetchData(), 1000);
      } else {
        message.error(response.message || 'Failed to start sync');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to start HP sync');
    } finally {
      setSyncingHp(false);
    }
  };

  const handleCancelHpSync = async () => {
    try {
      const response = await cancelHpSync();
      message.success(response.message || 'Sync cancelled');
      setTimeout(() => fetchData(), 1000);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      message.error(err?.response?.data?.message || 'Failed to cancel sync');
    }
  };

  const hasRunningHpJob = hpSyncJobs.some(j =>
    ['running', 'pending', 'importing', 'importing_metadata'].includes(j.status)
  );

  const refreshHpJobs = async () => {
    try {
      const result = await getHpSyncJobs();
      setHpSyncJobs(result.jobs || []);
    } catch (error) {
      console.error('Failed to refresh HP sync jobs:', error);
    }
  };

  const handleGenerateHpKey = async () => {
    Modal.confirm({
      title: 'Generate New SSH Key',
      content: hpPublicKey
        ? 'This will replace the existing SSH key pair. The old key will no longer work for authentication. Continue?'
        : 'This will generate a new Ed25519 SSH key pair. You will need to add the public key to the SFTP server.',
      okText: 'Generate Key',
      onOk: async () => {
        setGeneratingKey(true);
        try {
          const result = await generateHpSshKey();
          if (result.status === 'success' && result.public_key) {
            setHpPublicKey(result.public_key);
            hpForm.setFieldsValue({ auth_method: 'key' });
            message.success('SSH key pair generated. Copy the public key to the SFTP server.');
          } else {
            message.error(result.message || 'Failed to generate key');
          }
        } catch (error) {
          console.error('Failed to generate SSH key:', error);
          message.error('Failed to generate SSH key');
        } finally {
          setGeneratingKey(false);
        }
      },
    });
  };

  const copyPublicKey = () => {
    if (hpPublicKey) {
      navigator.clipboard.writeText(hpPublicKey);
      message.success('Public key copied to clipboard');
    }
  };

  const handleToggleProvider = async (providerName: string, enabled: boolean) => {
    try {
      await setProviderEnabled(providerName, enabled);
      setProviderStatus(prev => ({
        ...prev,
        [providerName]: { ...prev[providerName], enabled },
      }));
      message.success(`${providerStatus[providerName]?.display_name || providerName} ${enabled ? 'enabled' : 'disabled'}`);
    } catch {
      message.error('Failed to update provider');
    }
  };

  const renderEnabledSwitch = (providerName: string) => (
    <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
      <Switch
        checked={providerStatus[providerName]?.enabled ?? true}
        onChange={(checked) => handleToggleProvider(providerName, checked)}
      />
      <Text strong>Enabled</Text>
      {providerStatus[providerName]?.enabled === false && (
        <Text type="secondary"> — this provider is excluded from all lookups</Text>
      )}
    </div>
  );

  const getStatusTag = (status: ProviderStatus | undefined) => {
    if (!status?.enabled) {
      return <Tag icon={<CloseCircleOutlined />} color="warning">Disabled</Tag>;
    }
    return status?.available ? (
      <Tag icon={<CheckCircleOutlined />} color="success">Connected</Tag>
    ) : (
      <Tag icon={<CloseCircleOutlined />} color="default">Not Configured</Tag>
    );
  };

  const getTiebreakerTooltip = (priority: number) => (
    <Tooltip title="Used as tiebreaker when quality scores are equal">
      <Text type="secondary" style={{ marginLeft: 8 }}>
        <InfoCircleOutlined style={{ marginRight: 4 }} />
        Tiebreaker: {priority}
      </Text>
    </Tooltip>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading provider settings...</div>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'icecat',
      label: (
        <Space>
          <CloudOutlined />
          IceCat
          {getStatusTag(providerStatus.icecat)}
        </Space>
      ),
      children: (
        <div style={{ padding: '16px 0' }}>
          {renderEnabledSwitch('icecat')}
          <Space style={{ marginBottom: 16 }}>
            {getTiebreakerTooltip(providerStatus.icecat?.priority || 70)}
          </Space>

          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            IceCat provides rich product data for consumer electronics and IT products.
            Sign up at <a href="https://icecat.biz" target="_blank" rel="noopener noreferrer">icecat.biz</a> to get API credentials.
          </Paragraph>

          <Form
            form={icecatForm}
            layout="vertical"
            onFinish={handleIcecatSave}
            initialValues={icecatSettings || {}}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="username"
                  label="Username (Shop Name)"
                  tooltip="Your IceCat shop/account name"
                >
                  <Input placeholder="your_shop_name" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="app_key"
                  label="API Key"
                  tooltip="Your IceCat API token"
                >
                  <Input.Password placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={saving}
                >
                  Save Settings
                </Button>
                <Button
                  icon={<SyncOutlined spin={testingIcecat} />}
                  onClick={handleTestIcecat}
                  loading={testingIcecat}
                >
                  Test Connection
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    if (icecatSettings) {
                      icecatForm.setFieldsValue(icecatSettings);
                    }
                  }}
                >
                  Reset
                </Button>
              </Space>
            </Form.Item>
          </Form>

          <Divider orientation="left">Cache Management</Divider>

          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={8}>
              <Statistic
                title="Cached Products"
                value={icecatCacheStats?.cached_products || 0}
              />
            </Col>
            <Col xs={12} sm={8}>
              <Tooltip title="Products cached more than 180 days ago. They will be automatically refreshed from IceCat when accessed.">
                <Statistic
                  title={<span>Expired <InfoCircleOutlined style={{ fontSize: 12, color: '#888' }} /></span>}
                  value={icecatCacheStats?.expired_products || 0}
                />
              </Tooltip>
            </Col>
          </Row>

          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Product data is cached locally to reduce API calls. Use the button below to clear all cached
            IceCat data if you need to force a refresh. You can also refresh individual products from
            their detail page.
          </Paragraph>

          <Popconfirm
            title="Clear IceCat Cache"
            description="This will delete all cached IceCat product data. Products will be re-fetched from the API on next access."
            onConfirm={handleClearCache}
            okText="Clear Cache"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={clearingCache}
              disabled={!icecatCacheStats?.cached_products}
            >
              Clear All Cached Data
            </Button>
          </Popconfirm>

          <Divider orientation="left">Search IceCat</Divider>

          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Check if a product exists in IceCat by searching with MPN + Brand or GTIN.
            If found, the product will be cached locally.
          </Paragraph>

          <Form
            form={searchForm}
            layout="inline"
            onFinish={handleSearch}
            style={{ marginBottom: 16 }}
          >
            <Form.Item name="mpn" style={{ marginBottom: 8 }}>
              <Input placeholder="MPN (Part Number)" style={{ width: 180 }} />
            </Form.Item>
            <Form.Item name="brand" style={{ marginBottom: 8 }}>
              <Input placeholder="Brand" style={{ width: 150 }} />
            </Form.Item>
            <Form.Item style={{ marginBottom: 8 }}>
              <Text type="secondary">or</Text>
            </Form.Item>
            <Form.Item name="gtin" style={{ marginBottom: 8 }}>
              <Input placeholder="GTIN/UPC/EAN" style={{ width: 160 }} />
            </Form.Item>
            <Form.Item style={{ marginBottom: 8 }}>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SearchOutlined />}
                loading={searching}
              >
                Search
              </Button>
            </Form.Item>
          </Form>

          {searchResult && (
            <Alert
              type={searchResult.found ? 'success' : 'warning'}
              message={searchResult.found ? 'Product found and cached!' : 'Product not found in IceCat'}
              description={searchResult.found && searchResult.product
                ? `MPN: ${(searchResult.product as { mfgpartno?: string }).mfgpartno || 'N/A'}`
                : 'Try a different MPN/Brand combination or GTIN'}
              style={{ marginBottom: 16 }}
              closable
              onClose={() => setSearchResult(null)}
            />
          )}

          <Divider orientation="left">Pre-seed Manufacturer</Divider>

          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Fetch all available products from a manufacturer and cache them locally.
            This runs as a background job and may take some time for large catalogs.
          </Paragraph>

          <Form
            form={preseedForm}
            layout="inline"
            onFinish={handlePreseed}
            style={{ marginBottom: 16 }}
          >
            <Form.Item
              name="brand"
              rules={[{ required: true, message: 'Brand is required' }]}
            >
              <Input placeholder="Brand name (e.g., HP, Dell, Cisco)" style={{ width: 250 }} />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<DownloadOutlined />}
                loading={preseeding}
              >
                Start Pre-seed
              </Button>
            </Form.Item>
          </Form>

          {preseedJobs.length > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong>Pre-seed Job History</Text>
                <Button size="small" icon={<ReloadOutlined />} onClick={refreshPreseedJobs}>
                  Refresh
                </Button>
              </div>
              <Table
                dataSource={preseedJobs}
                rowKey="id"
                size="small"
                pagination={{ pageSize: 5 }}
                columns={[
                  {
                    title: 'Brand',
                    dataIndex: 'brand',
                    key: 'brand',
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    render: (status: string) => {
                      const colors: Record<string, string> = {
                        pending: 'default',
                        running: 'processing',
                        completed: 'success',
                        failed: 'error',
                      };
                      return <Badge status={colors[status] as 'default' | 'processing' | 'success' | 'error'} text={status} />;
                    },
                  },
                  {
                    title: 'Progress',
                    key: 'progress',
                    render: (_: unknown, record: IcecatPreseedJob) => (
                      <Text type="secondary">
                        {record.products_fetched}/{record.products_found || '?'} fetched
                        {record.products_failed > 0 && `, ${record.products_failed} failed`}
                      </Text>
                    ),
                  },
                  {
                    title: 'Started',
                    dataIndex: 'started_at',
                    key: 'started_at',
                    render: (date: string) => formatTimestamp(date),
                  },
                ]}
              />
            </>
          )}

          <Divider orientation="left">Backfill from Other Providers</Divider>

          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Enrich existing products from NIQ Brandbank or CSV imports with IceCat data.
            This will search IceCat for each product and cache the results.
            {queueStats && queueStats.pending > 0 && (
              <Alert
                type="info"
                message={`${queueStats.pending} products pending IceCat lookup`}
                style={{ marginTop: 8 }}
                showIcon
              />
            )}
            {queueStats && queueStats.completed > 0 && (
              <Alert
                type="success"
                message={
                  <span>
                    Lookup complete: <strong>{queueStats.found}</strong> found
                    {queueStats.not_found > 0 && <>, <strong>{queueStats.not_found}</strong> not in IceCat</>}
                    {queueStats.premium_only > 0 && (
                      <Tooltip title="These products exist in IceCat but require a premium subscription to access">
                        <span>, <strong>{queueStats.premium_only}</strong> premium only <InfoCircleOutlined /></span>
                      </Tooltip>
                    )}
                  </span>
                }
                style={{ marginTop: 8 }}
                showIcon
              />
            )}
          </Paragraph>

          <Form
            form={backfillForm}
            layout="inline"
            onFinish={handleBackfill}
            style={{ marginBottom: 16 }}
          >
            <Form.Item
              name="source_provider"
              rules={[{ required: true, message: 'Select a source' }]}
              initialValue="etilize"
            >
              <Select style={{ width: 220 }}>
                <Select.Option value="etilize">NIQ Brandbank Products</Select.Option>
                <Select.Option value="csv">CSV Import Products</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SyncOutlined />}
                loading={backfilling}
              >
                Start Backfill
              </Button>
            </Form.Item>
          </Form>

          {backfillJobs.length > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text strong>Backfill Job History</Text>
                <Button size="small" icon={<ReloadOutlined />} onClick={refreshBackfillJobs}>
                  Refresh
                </Button>
              </div>
              <Table
                dataSource={backfillJobs}
                rowKey="id"
                size="small"
                pagination={{ pageSize: 5 }}
                columns={[
                  {
                    title: 'Source',
                    dataIndex: 'source_provider',
                    key: 'source_provider',
                    render: (value: string) => {
                      const names: Record<string, string> = {
                        etilize: 'NIQ Brandbank',
                        external: 'CSV Import',
                      };
                      return names[value] || value;
                    },
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    render: (status: string) => {
                      const colors: Record<string, string> = {
                        pending: 'default',
                        running: 'processing',
                        completed: 'success',
                        failed: 'error',
                      };
                      return <Badge status={colors[status] as 'default' | 'processing' | 'success' | 'error'} text={status} />;
                    },
                  },
                  {
                    title: 'Progress',
                    key: 'progress',
                    render: (_: unknown, record: IcecatBackfillJob) => (
                      <Text type="secondary">
                        {record.products_found} found
                        {record.products_not_found > 0 && `, ${record.products_not_found} not found`}
                        {record.products_failed > 0 && ` (${record.products_failed} failed)`}
                        {record.error_message && record.error_message.includes('premium') && (
                          <Tooltip title={record.error_message}>
                            <InfoCircleOutlined style={{ marginLeft: 4, color: '#faad14' }} />
                          </Tooltip>
                        )}
                      </Text>
                    ),
                  },
                  {
                    title: 'Started',
                    dataIndex: 'started_at',
                    key: 'started_at',
                    render: (date: string) => formatTimestamp(date),
                  },
                ]}
              />
            </>
          )}
        </div>
      ),
    },
    {
      key: 'etilize',
      label: (
        <Space>
          <DatabaseOutlined />
          NIQ Brandbank
          {getStatusTag(providerStatus.etilize)}
        </Space>
      ),
      children: (
        <div style={{ padding: '16px 0' }}>
          {renderEnabledSwitch('etilize')}
          <Space style={{ marginBottom: 16 }}>
            {getTiebreakerTooltip(providerStatus.etilize?.priority || 80)}
          </Space>

          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            NIQ Brandbank provides comprehensive product data via FTP sync.
            Contact your NIQ representative for access credentials.
          </Paragraph>

          <Form
            form={etlForm}
            layout="vertical"
            onFinish={handleEtlSave}
            initialValues={etlSettings || {}}
          >
            <Divider orientation="left">Sync Mode</Divider>

            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="client_mode"
                  label="Sync Mode"
                  tooltip="DELTA syncs only changes since last sync. FULL downloads all data."
                >
                  <Select>
                    <Select.Option value="DELTA">Delta (Changes Only)</Select.Option>
                    <Select.Option value="FULL">Full Sync</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="client_locale"
                  label="Locale"
                  tooltip="Language/region for product data"
                >
                  <Select>
                    <Select.Option value="EN_US">English (US)</Select.Option>
                    <Select.Option value="EN_GB">English (UK)</Select.Option>
                    <Select.Option value="FR_FR">French</Select.Option>
                    <Select.Option value="DE_DE">German</Select.Option>
                    <Select.Option value="ES_ES">Spanish</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="connection_retries"
                  label="Connection Retries"
                  tooltip="Number of retry attempts for FTP connection issues"
                >
                  <InputNumber min={1} max={50} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="cleanup_on_success"
              label="Cleanup After Success"
              valuePropName="checked"
              tooltip="Delete downloaded files after successful import."
            >
              <Switch />
            </Form.Item>

            <Divider orientation="left">FTP Server</Divider>

            <Alert
              type="info"
              message="FTP credentials are provided by NIQ/Etilize"
              style={{ marginBottom: 16 }}
            />

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="ftp_host"
                  label="FTP Host"
                  rules={[{ required: true, message: 'FTP host is required' }]}
                >
                  <Input placeholder="ftp.etilize.com" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="ftp_directory"
                  label="FTP Directory"
                >
                  <Input placeholder="/" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="ftp_username"
                  label="FTP Username"
                  rules={[{ required: true, message: 'FTP username is required' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="ftp_password"
                  label="FTP Password"
                  rules={[{ required: true, message: 'FTP password is required' }]}
                >
                  <Input.Password placeholder="********" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={saving}
                >
                  Save Settings
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    if (etlSettings) {
                      etlForm.setFieldsValue(etlSettings);
                    }
                  }}
                >
                  Reset
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      ),
    },
    {
      key: 'hp',
      label: (
        <Space>
          <DownloadOutlined />
          HP
          {getStatusTag(providerStatus.hp)}
        </Space>
      ),
      children: (
        <div style={{ padding: '16px 0' }}>
          {renderEnabledSwitch('hp')}
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            HP provides product data via SFTP as compressed XML archives.
            Configure SFTP credentials to sync product data from the HP relay server.
          </Paragraph>

          <Form
            form={hpForm}
            layout="vertical"
            onFinish={handleHpSave}
            initialValues={hpSettings || {}}
          >
            <Divider orientation="left">SFTP Server</Divider>

            <Row gutter={16}>
              <Col xs={24} sm={10}>
                <Form.Item
                  name="sftp_host"
                  label="SFTP Host"
                  rules={[{ required: true, message: 'SFTP host is required' }]}
                >
                  <Input placeholder="sftp.example.com" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={4}>
                <Form.Item
                  name="sftp_port"
                  label="Port"
                >
                  <InputNumber min={1} max={65535} style={{ width: '100%' }} placeholder="22" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={10}>
                <Form.Item
                  name="sftp_remote_dir"
                  label="Remote Directory"
                  tooltip="Root directory on the SFTP server to scan for files"
                >
                  <Input placeholder="/" />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">Authentication</Divider>

            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item
                  name="auth_method"
                  label="Auth Method"
                >
                  <Select>
                    <Select.Option value="password">Password</Select.Option>
                    <Select.Option value="key">SSH Key</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} sm={16}>
                <Form.Item
                  name="sftp_username"
                  label="Username"
                  rules={[{ required: true, message: 'Username is required' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.auth_method !== cur.auth_method}>
              {({ getFieldValue }) => {
                const authMethod = getFieldValue('auth_method') || 'password';
                if (authMethod === 'password') {
                  return (
                    <Form.Item
                      name="sftp_password"
                      label="Password"
                      rules={[{ required: true, message: 'Password is required' }]}
                    >
                      <Input.Password placeholder="********" />
                    </Form.Item>
                  );
                }
                return (
                  <div>
                    <Space style={{ marginBottom: 12 }}>
                      <Button
                        icon={<KeyOutlined />}
                        loading={generatingKey}
                        onClick={handleGenerateHpKey}
                      >
                        {hpPublicKey ? 'Regenerate SSH Key' : 'Generate SSH Key'}
                      </Button>
                    </Space>
                    {hpPublicKey && (
                      <Alert
                        type="success"
                        message="SSH Public Key"
                        description={
                          <div>
                            <Paragraph>
                              Add this public key to the SFTP server&apos;s <Text code>authorized_keys</Text> file:
                            </Paragraph>
                            <div style={{ position: 'relative' }}>
                              <Input.TextArea
                                value={hpPublicKey}
                                readOnly
                                autoSize={{ minRows: 2, maxRows: 4 }}
                                style={{ fontFamily: 'monospace', fontSize: 12 }}
                              />
                              <Button
                                icon={<CopyOutlined />}
                                size="small"
                                onClick={copyPublicKey}
                                style={{ position: 'absolute', top: 4, right: 4 }}
                              >
                                Copy
                              </Button>
                            </div>
                          </div>
                        }
                        style={{ marginBottom: 16 }}
                      />
                    )}
                    {!hpPublicKey && (
                      <Alert
                        type="info"
                        message="No SSH key generated yet. Click the button above to create one."
                        style={{ marginBottom: 16 }}
                      />
                    )}
                  </div>
                );
              }}
            </Form.Item>

            <Divider orientation="left">Sync Schedule</Divider>

            <Row gutter={16} align="middle">
              <Col xs={24} sm={6}>
                <Form.Item
                  name="auto_sync_enabled"
                  label="Auto Sync"
                  valuePropName="checked"
                  tooltip="Automatically check for new HP archives on a schedule"
                >
                  <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item
                  name="sync_interval_hours"
                  label="Interval (hours)"
                  tooltip="How often to check for new archives (minimum 1 hour)"
                >
                  <InputNumber min={1} max={168} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6} style={{ paddingTop: 4 }}>
                {hasRunningHpJob ? (
                  <Button
                    danger
                    icon={<StopOutlined />}
                    onClick={handleCancelHpSync}
                  >
                    Cancel Sync
                  </Button>
                ) : (
                  <Tooltip title="Download, extract, and parse new archives now">
                    <Button
                      icon={<SyncOutlined />}
                      loading={syncingHp}
                      onClick={handleHpSync}
                    >
                      Force Sync
                    </Button>
                  </Tooltip>
                )}
              </Col>
            </Row>

            <Divider orientation="left">Local Paths</Divider>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="download_path"
                  label="Download Path"
                  tooltip="Local directory where downloaded archives are stored temporarily"
                >
                  <Input placeholder="/data/HP/Download" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="processing_path"
                  label="Processing Path"
                  tooltip="Local directory where extracted files are placed for processing"
                >
                  <Input placeholder="/data/HP/Processing" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={saving}
                >
                  Save Settings
                </Button>
                <Button
                  icon={<SyncOutlined />}
                  loading={testingHp}
                  onClick={handleTestHp}
                >
                  Test Connection
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    if (hpSettings) {
                      hpForm.setFieldsValue(hpSettings);
                    }
                  }}
                >
                  Reset
                </Button>
              </Space>
            </Form.Item>
          </Form>

          {hpStats && hpStats.total_products > 0 && (
            <>
              <Divider orientation="left">Product Database</Divider>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                  <Statistic title="Total Products" value={hpStats.total_products} />
                </Col>
                <Col span={6}>
                  <Statistic title="With Images" value={hpStats.products_with_images} />
                </Col>
                <Col span={6}>
                  <Statistic title="Avg Quality" value={hpStats.avg_quality_score} suffix="/ 100" />
                </Col>
                <Col span={6}>
                  <Statistic title="Categories" value={hpStats.by_category?.length || 0} />
                </Col>
              </Row>
              {hpStats.by_category && hpStats.by_category.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  {hpStats.by_category.map((cat) => (
                    <Tag key={cat.category_name} style={{ marginBottom: 4 }}>
                      {cat.category_name}: {cat.cnt.toLocaleString()}
                    </Tag>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Operations (Import Products, Import Metadata, Re-extract) removed from UI.
              These are still accessible via API endpoints if needed for recovery. */}

          {hpSyncJobs.length > 0 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, marginTop: 16 }}>
                <Text strong>Job History</Text>
                <Button size="small" icon={<ReloadOutlined />} onClick={refreshHpJobs}>
                  Refresh
                </Button>
              </div>
              <Table
                dataSource={hpSyncJobs}
                rowKey="id"
                size="small"
                pagination={{ pageSize: 5 }}
                columns={[
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    width: 140,
                    render: (status: string) => {
                      const colors: Record<string, string> = {
                        pending: 'default',
                        running: 'processing',
                        importing: 'processing',
                        importing_metadata: 'processing',
                        completed: 'success',
                        failed: 'error',
                        interrupted: 'warning',
                      };
                      return <Badge status={(colors[status] || 'default') as 'default' | 'processing' | 'success' | 'error' | 'warning'} text={status} />;
                    },
                  },
                  {
                    title: 'Found',
                    dataIndex: 'files_found',
                    key: 'files_found',
                  },
                  {
                    title: 'Downloaded',
                    dataIndex: 'files_downloaded',
                    key: 'files_downloaded',
                  },
                  {
                    title: 'Extracted',
                    dataIndex: 'files_extracted',
                    key: 'files_extracted',
                  },
                  {
                    title: 'Failed',
                    dataIndex: 'files_failed',
                    key: 'files_failed',
                    render: (val: number, record: HpSyncJob) => (
                      <>
                        {val}
                        {record.error_message && (
                          <Tooltip title={record.error_message}>
                            <InfoCircleOutlined style={{ marginLeft: 4, color: '#ff4d4f' }} />
                          </Tooltip>
                        )}
                      </>
                    ),
                  },
                  {
                    title: 'Started',
                    dataIndex: 'started_at',
                    key: 'started_at',
                    render: (date: string) => formatTimestamp(date),
                  },
                ]}
              />
            </>
          )}
        </div>
      ),
    },
    {
      key: 'external',
      label: (
        <Space>
          <ApiOutlined />
          External / CSV
          {getStatusTag(providerStatus.external)}
        </Space>
      ),
      children: (
        <div style={{ padding: '16px 0' }}>
          {renderEnabledSwitch('external')}
          <Space style={{ marginBottom: 16 }}>
            {getTiebreakerTooltip(providerStatus.external?.priority || 60)}
          </Space>

          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            External provider handles data imported via CSV files.
            This provider is always available and requires no configuration.
          </Paragraph>

          <Alert
            type="info"
            message="CSV imports can be performed from the Data Import page."
          />
        </div>
      ),
    },
    {
      key: 'psref',
      label: (
        <Space>
          <CloudOutlined />
          Lenovo PSREF
          {getStatusTag(providerStatus.psref)}
        </Space>
      ),
      children: (
        <div style={{ padding: '16px 0' }}>
          {renderEnabledSwitch('psref')}
          <Space style={{ marginBottom: 16 }}>
            {getTiebreakerTooltip(providerStatus.psref?.priority || 72)}
          </Space>

          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Lenovo PSREF (Product Specifications Reference) provides detailed specs, photos, documentation,
            and 360&deg; videos for Lenovo commercial and consumer products including ThinkPad, ThinkStation,
            ThinkVision, IdeaPad, Yoga, and Legion. No API credentials required.
          </Paragraph>

          <Alert
            type="warning"
            message="Content Licensing"
            description="PSREF content (images, videos, PDFs, specifications) is copyrighted by Lenovo. Confirm redistribution rights via your Lenovo partner agreement before enabling this provider."
            style={{ marginBottom: 16 }}
          />

          <Alert
            type="info"
            message="This provider uses the public PSREF API at psrefapi.lenovo.com. Results are cached locally with version-aware invalidation."
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Configure data providers for product information. When looking up a product, all available
        providers are queried and the result with the highest quality score (based on data completeness)
        is returned. If multiple providers have equal quality scores, the tiebreaker value determines
        the winner. You can view alternative provider data on product detail pages.
      </Paragraph>

      <Card>
        <Tabs
          items={tabItems}
          activeKey={activeTab}
          onChange={handleTabChange}
          tabPosition="left"
          style={{ minHeight: 400 }}
        />
      </Card>
    </div>
  );
}
